import React, { forwardRef, useEffect, useRef, useState } from "react";
import style from "./TeamDesktop.module.scss";
import gsap from "gsap";
import CustomEase from "gsap/dist/CustomEase";
import Link from "next/link";
import { Icon } from "@/components/common/Icons/Icon";
import ScrollToPlugin from "gsap/ScrollToPlugin";
import { MagneticCursor } from "@/components/common/MagneticCursor/MagneticCursor";
import { TitleAnimation } from "@/components/common/TitleAnimation/TitleAnimation";
import { API_BASE } from "../../../../constants/config";
import { useScrollStore } from "@/components/common/ScrollManager/useScrollStore";
import { PixelEvents } from "@/components/Pixel/PixelManager";

//TEMPORARY function to create the rows as desired
interface UserProfile {
  name: string;
  horizontal_name: string;
  rol: string;
  description: string;
  linkedin: string;
  metrics: [string, string][];
  image: string;
  horizontal_image: string;
}

gsap.registerPlugin(ScrollToPlugin);

export default function TeamDesktop({ messages }) {
  const { scroller } = useScrollStore();

  //Reference used for the effect
  const cardsContainerRef = useRef(null);
  const cardsContentRef = useRef(null);
  const cardsBgImageRef = useRef(null);
  const tempOverlay = useRef(null);

  const [cardData, setCardData] = useState(null);

  //State to know if a card is open
  const [isCardOpened, setIsCardOpened] = useState(false);

  //Register GSAP Plugin
  gsap.registerPlugin(CustomEase);

  //Create custom easing for the "inertia" effect
  CustomEase.create(
    "Inertia",
    "M0,0 C0.126,0.382 0.561,1 0.8,1 1.246,1 0.818,1.001 1,1"
  );

  const [widthExceded, setWidthExceded] = useState(false);

  const createRows = (data: UserProfile[]) => {
    const rowOne = data.slice(6, 12);
    const rowTwo = data.slice(12, 18);
    const rowThree = data.slice(18, 23);

    return [rowOne, rowTwo, rowThree];
  };

  const rows = createRows(messages.team);

  //Function for the open onClick card effect
  const handleCardOpen = (
    cardRef,
    cardsContentRef,
    cardsBgImageRef,
    tempOverlay,
    setClickProgress,
    widthExceded
  ) => {
    scroller?.stop();
    //Card reference
    const card = cardRef;

    //Card values
    const cardRect = card.getBoundingClientRect();

    const viewportCenterX = window.innerWidth / 2;
    const viewportCenterY = window.innerHeight / 2;

    const cardCenterX = cardRect.left + cardRect.width / 2;
    const cardCenterY = cardRect.top + cardRect.height / 2;

    let translateX = 0;

    if (!widthExceded) {
      translateX = viewportCenterX - cardCenterX;
    }

    let translateY = viewportCenterY - cardCenterY;
    let cardOpenWidth = 450;
    let cardOpenHeight = 265;

    const containerCard = document.querySelector(`.${style.cardsContainer}`);
    const containerRect = containerCard.getBoundingClientRect();

    let scrollYPosition = 0;

    //Handle cards Y overflow based on the user scroll position
    if (
      containerRect.top > 130 ||
      containerRect.bottom < window.innerHeight - 130
    ) {
      translateY = 0;

      const cardCenterY = cardRect.top + cardRect.height / 2;
      const viewportCenterY = window.innerHeight / 2;

      if (containerRect.top > 130) {
        scrollYPosition = cardCenterY - viewportCenterY;
      } else if (containerRect.bottom < window.innerHeight - 130) {
        scrollYPosition = cardCenterY - (window.innerHeight - viewportCenterY);
      }
    }

    let finalOpenWidth = `${cardOpenWidth}px`;
    let finalOpenHeight = `${cardOpenHeight}px`;

    //Kill any animation running
    gsap.killTweensOf({ cardsContentRef, cardsBgImageRef, card });

    const timeline = gsap.timeline();

    //Start card open animation
    timeline
      .to(
        cardsContentRef,
        {
          x: `+=${translateX}`,
          y: `+=${translateY}`,
          duration: 0.45,
          ease: "Inertia",
        },
        "1"
      )
      .to(
        cardsBgImageRef,
        {
          x: `+=${translateX}`,
          y: `+=${translateY}`,
          duration: 0.45,
          ease: "Inertia",
        },
        "1"
      )
      .to(
        window,
        {
          duration: 0.15,
          scrollTo: {
            y: `+=${scrollYPosition}`,
            autoKill: false,
          },
        },
        "<-=0.15"
      )
      .to(
        card,
        {
          zIndex: 20,
          duration: 0,
        },
        "1"
      )
      .to(
        tempOverlay.current,
        {
          x: `${-translateX}px`,
          opacity: 0.5,
          zIndex: 15,
          duration: 0.2,
        },
        "1"
      )
      .to(
        card.children[1],
        {
          height: "100%",
          opacity: 0,
          duration: 0.125,
        },
        "1"
      )
      .to(
        card.children[0],
        {
          rotateY: "180deg",
          duration: 0.25,
        },
        "1"
      )
      .to(card.children[0], {
        width: finalOpenWidth,
        height: finalOpenHeight,
        duration: 0.125,
      })
      .to(card.children[1], {
        opacity: 1,
        duration: 0,
      })
      .to(card.children[1], {
        height: "102%",
        duration: 0.125,
      })
      .to(card.children[0].children[1].children[0], {
        opacity: 1,
        duration: 0.125,
        onComplete: () => {
          setClickProgress(false);
        },
      });
  };

  //Function for the close onClick card effect
  const handleCardClose = (
    cardRef,
    isHorizontal,
    windowProps,
    tempOverlay,
    setIsCardOpened,
    setClickProgress,
    widthExceded,
    cardsContentRef,
    cardsBgImageRef
  ) => {
    //Card reference stored
    const card = cardRef;

    let width: string, height: string;

    height = isHorizontal ? `266px` : `381px`;
    width = isHorizontal ? `450px` : `265px`;

    //Start the close animation
    gsap
      .timeline()
      .to(card.children[0].children[1].children[0], {
        opacity: 0,
        duration: 0.15,
      })
      .to(card.children[1], {
        height: "100%",
        duration: 0.15,
      })
      .to(card.children[1], {
        opacity: 0,
        duration: 0,
      })
      .to(card.children[0], {
        width: width,
        height: height,
        duration: 0.375,
      })
      .to(card.children[0], {
        rotateY: "0deg",
        duration: 0.375,
      })
      .to(card.children[1], {
        opacity: 1,
        duration: 0.15,
      })
      .to(card.children[1], {
        height: "102%",
        duration: 0.15,
      })
      .to(tempOverlay.current, {
        x: 0,
        opacity: 0,
        zIndex: -1,
        duration: 0.125,
      })
      .to(card, {
        zIndex: 5,
        duration: 0,
      })
      .to(
        cardsContentRef,
        {
          transform: "translate(-50%, -50%)",
          duration: 0.45,
          ease: "Inertia",
        },
        "end"
      )
      .to(
        cardsBgImageRef,
        {
          transform: "translate(-50%, -50%)",
          duration: 0.45,
          ease: "Inertia",
          onComplete: () => {
            setIsCardOpened(false);
            setClickProgress(false);
            scroller?.start();
          },
        },
        "end"
      );
  };

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== undefined) {
        setWidthExceded(window.innerWidth >= 2174);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  //State to check if there's any animation already happening
  const [clickProgress, setClickProgress] = useState(false);

  //Function to trigger the onClick card effect
  const cardOnClick = (cardRef, container, isHorizontal, windowProps) => {
    if (clickProgress) {
      return;
    }
    setClickProgress(true);
    //If there's no card open

    setCardData({
      cardRef,
      isHorizontal,
      windowProps,
    });

    if (!isCardOpened) {
      setIsCardOpened(true);
      handleCardOpen(
        cardRef,
        cardsContentRef.current,
        cardsBgImageRef.current,
        tempOverlay,
        setClickProgress,
        widthExceded
      );
      return;
    }
    //If there's a card open
    if (isCardOpened) {
      handleCardClose(
        cardRef,
        isHorizontal,
        windowProps,
        tempOverlay,
        setIsCardOpened,
        setClickProgress,
        widthExceded,
        cardsContentRef.current,
        cardsBgImageRef.current
      );
    }
  };

  useEffect(() => {
    //Function to make the onMouseMove effect
    const handleMouseMove = (event) => {
      //Stop doing the mouseMove effect if there's a card open
      if (!isCardOpened) {
        //Store the container and the content
        const containerRect = cardsContainerRef.current.getBoundingClientRect();
        const contentRect = cardsContentRef.current.getBoundingClientRect();

        if (containerRect && contentRect) {
          const containerCenterX = containerRect.left + containerRect.width / 2;
          const containerCenterY = containerRect.top + containerRect.height / 2;

          const offsetX = event.clientX - containerCenterX;
          const offsetY = event.clientY - containerCenterY;
          //Get the total distance the content can move in the X direction
          const speedX =
            (Math.abs(offsetX) / containerRect.width) *
            (contentRect.width - containerRect.width);

          const speedY =
            (Math.abs(offsetY) / containerRect.height) *
            (contentRect.height - containerRect.height);

          const signX = offsetX > 0 ? -1 : 1;
          const signY = offsetY > 0 ? 1 : -1;
          let cardsTranslateX, cardsTranslateY;
          if (widthExceded) {
            cardsTranslateX = `${signX * (speedX / 1.1)}px`;
            cardsTranslateY = `${signY * (speedY / 1)}px`;
          } else {
            cardsTranslateX = `${signX * (speedX * 1.2)}px`;
            cardsTranslateY = `${signY * (speedY / 0.5)}px`;
          }

          const imageTranslateX = `${signX * (speedX / 4)}px`;
          const imageTranslateY = `${signY * (speedY / 4)}px`;

          // Apply the movement with GSAP
          gsap.to(cardsContentRef.current, {
            transform: `translate(calc(-50% + ${cardsTranslateX}), calc(-50% + ${cardsTranslateY}))`,
            duration: 1,
            ease: "Inertia",
          });

          gsap.to(cardsBgImageRef.current, {
            transform: `translate(calc(-50% + ${imageTranslateX}), calc(-50% + ${imageTranslateY}))`,
            duration: 1,
            ease: "Inertia",
          });
        }
      }
    };

    //Store the reference in a constant to avoid React hydration errors
    const cardsContainer = cardsContainerRef.current;

    if (cardsContainer) {
      // Set the event listener
      cardsContainer.addEventListener("mousemove", handleMouseMove);

      // Remove the event listener on cleanup
      return () => {
        cardsContainer.removeEventListener("mousemove", handleMouseMove);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCardOpened, widthExceded]);

  //State to conditionally render the cards
  const [showCards, setShowCards] = useState(false);

  useEffect(() => {
    if (cardsContainerRef.current !== null) {
      setShowCards(true);
    }
  }, [cardsContainerRef]);

  const overlayTrigger = () => {
    if (isCardOpened && cardData !== null) {
      handleCardClose(
        cardData.cardRef,
        cardData.isHorizontal,
        cardData.windowProps,
        tempOverlay,
        setIsCardOpened,
        setClickProgress,
        widthExceded,
        cardsContentRef.current,
        cardsBgImageRef.current
      );
    }
  };

  if (messages?.team?.length === 0) return null;

  return (
    <div className={`${style.sectionTeam}`}>
      <div className={`${style.containerTeam}`}>
        <div className={`container`}>
          <TitleAnimation
            messages={{
              first_word: messages.first_word_all,
              second_word: messages.second_word_all,
            }}
            isOneLine
          />
        </div>
        <div className={`${style.cardsContainer}`} ref={cardsContainerRef}>
          <div className={`${style.cardsContent}`} ref={cardsContentRef}>
            {showCards &&
              rows.map((row, j) => {
                return (
                  <div className={`${style.cardsRow}`} key={j}>
                    {row.map((item, i) => (
                      <Card
                        img={item.image}
                        horizontal_img={item.horizontal_image}
                        name={item.name}
                        role={item.rol}
                        information={item.description}
                        class={`card${i} card`}
                        attrValue={""}
                        key={i}
                        linkedin={item.linkedin}
                        windowProps={{
                          height: cardsContainerRef.current.clientHeight,
                          width: cardsContainerRef.current.clientWidth,
                        }}
                        isHorizontal={
                          j === 1
                            ? i % 2 === 0
                              ? false
                              : true
                            : i % 2 === 0
                              ? true
                              : false
                        }
                        container={cardsContainerRef}
                        clickFunction={cardOnClick}
                        openCard={isCardOpened}
                      />
                    ))}
                  </div>
                );
              })}
            <div
              className={`${style.cardOpenOverlay}`}
              ref={tempOverlay}
              onClick={() => overlayTrigger()}
              data-hover={isCardOpened ? "hcards" : null}
            ></div>
          </div>
          <div
            className={`${style.cardsImageBackground}`}
            ref={cardsBgImageRef}
          >
            {/* eslint-disable-next-line @next/next/no-img-element*/}
            <img src="/bg_team.webp" alt="Placeholder background" />
          </div>
        </div>
      </div>
    </div>
  );
}

const Card = forwardRef(
  (
    props: {
      name: string;
      role: string;
      img: string;
      horizontal_img: string;
      information: string;
      class: string;
      attrValue: string;
      isHorizontal: boolean;
      linkedin: string;
      windowProps: {
        height: number;
        width: number;
      };
      container: React.MutableRefObject<any>;
      clickFunction: any;
      openCard: boolean;
    },
    ref
  ) => {
    //Store the card
    const cardRef = useRef(null);

    //Store both container in constants
    const container = props.container.current;

    //Main function that handles the onClick card animation
    const triggerEffect = () => {
      props.clickFunction(
        cardRef.current,
        container,
        props.isHorizontal,
        props.windowProps
      );
    };

    const [widthExceded, setWidthExceded] = useState(false);

    useEffect(() => {
      if (typeof window !== undefined) {
        setWidthExceded(window.innerWidth >= 1901);
      }
    }, []);

    const baseCardStyle = {
      individual: {
        height: props.isHorizontal ? `265px` : `381px`,
        width: props.isHorizontal ? `450px` : `240px`,
      },
      card: {
        height: props.isHorizontal ? `265px` : `381px`,
        width: props.isHorizontal ? `450px` : `240px`,
      },
    };

    const extendedCardStyle = {
      individual: {
        height: props.isHorizontal ? `265px` : `381px`,
        width: props.isHorizontal ? `450px` : `240px`,
      },
      card: {
        height: props.isHorizontal ? `265px` : `381px`,
        width: props.isHorizontal ? `450px` : `240px`,
      },
    };

    return (
      <div
        className={`${style.individual}`}
        style={
          widthExceded ? extendedCardStyle.individual : baseCardStyle.individual
        }
        data-orientation={props.attrValue}
      >
        <div
          className={`${style.card} ${props.class}`}
          ref={cardRef}
          onClick={() => triggerEffect()}
          data-hover={props.openCard ? "hcards" : null}
        >
          <div
            className={`${style.card_inner}`}
            style={widthExceded ? extendedCardStyle.card : baseCardStyle.card}
          >
            <div
              className={
                props.isHorizontal ? style.front_hor : style.front_vert
              }
            >
              <div
                className={
                  props.isHorizontal
                    ? style.front_hor_content
                    : style.front_vert_content
                }
              >
                <div className={style.front_text}>
                  <h2>{props.name}</h2>
                  <p>{props.role}</p>
                </div>
                <picture className={style.front_image}>
                  <img
                    src={
                      API_BASE +
                      (props.isHorizontal ? props.horizontal_img : props.img)
                    }
                    alt={`Image of ${props.name}`}
                  />
                </picture>
              </div>
            </div>
            <div className={style.back} data-hover={"hcards"}>
              <div className={style.back_content}>
                <div className={style.back_content_information}>
                  <div>
                    <div>
                      <h3>{props.name}</h3>
                      <span>{props.role}</span>
                    </div>
                    {props.linkedin && (
                      <MagneticCursor>
                        <div
                          className={style.back_content_social}
                          data-hover="hcards-social"
                        >
                          <Link 
                            href={props.linkedin} 
                            target="_blank"
                            onClick={() => PixelEvents.clickLinkedInProfile(props.name, props.role)}
                          >
                            <Icon
                              name={"LinkedIn"}
                              fill={"var(--primary)"}
                            ></Icon>
                          </Link>
                        </div>
                      </MagneticCursor>
                    )}
                  </div>
                  <p>{props.information}</p>
                </div>
              </div>
            </div>
          </div>
          <div className={style.glow} data-hover={"hcards"}></div>
        </div>
      </div>
    );
  }
);

Card.displayName = "Card";
